import React, { useState, useEffect } from 'react';

interface EmailEntry {
  id: string;
  email: string;
  url: string;
  domain: string;
  createdAt: string;
}

interface Domain {
  id: string;
  domain: string;
  defaultEmail: string;
  isCatchAll: boolean;
}

export const EmailManager: React.FC = () => {
  const [emails, setEmails] = useState<EmailEntry[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [newEmail, setNewEmail] = useState({ email: '', url: '', domain: '' });
  const [newDomain, setNewDomain] = useState({ domain: '', defaultEmail: '', isCatchAll: false });
  const [showDomainForm, setShowDomainForm] = useState(false);

  useEffect(() => {
    // Load existing data
    const savedEmails = localStorage.getItem('email-entries');
    const savedDomains = localStorage.getItem('email-domains');
    
    if (savedEmails) {
      setEmails(JSON.parse(savedEmails));
    }
    
    if (savedDomains) {
      setDomains(JSON.parse(savedDomains));
    } else {
      // Add example domain
      const exampleDomains = [{
        id: '1',
        domain: 'oh.mom',
        defaultEmail: 'you@oh.mom',
        isCatchAll: true
      }];
      setDomains(exampleDomains);
      localStorage.setItem('email-domains', JSON.stringify(exampleDomains));
    }
  }, []);

  const saveEmails = (newEmails: EmailEntry[]) => {
    setEmails(newEmails);
    localStorage.setItem('email-entries', JSON.stringify(newEmails));
  };

  const saveDomains = (newDomains: Domain[]) => {
    setDomains(newDomains);
    localStorage.setItem('email-domains', JSON.stringify(newDomains));
  };

  const addEmail = () => {
    if (!newEmail.email || !newEmail.url) return;
    
    const entry: EmailEntry = {
      id: Date.now().toString(),
      email: newEmail.email,
      url: newEmail.url,
      domain: newEmail.domain || extractDomain(newEmail.email),
      createdAt: new Date().toISOString()
    };
    
    saveEmails([...emails, entry]);
    setNewEmail({ email: '', url: '', domain: '' });
  };

  const deleteEmail = (id: string) => {
    saveEmails(emails.filter(e => e.id !== id));
  };

  const addDomain = () => {
    if (!newDomain.domain || !newDomain.defaultEmail) return;
    
    const domain: Domain = {
      id: Date.now().toString(),
      ...newDomain
    };
    
    saveDomains([...domains, domain]);
    setNewDomain({ domain: '', defaultEmail: '', isCatchAll: false });
    setShowDomainForm(false);
  };

  const deleteDomain = (id: string) => {
    saveDomains(domains.filter(d => d.id !== id));
  };

  const extractDomain = (email: string) => {
    return email.split('@')[1] || '';
  };

  const generateEmail = (domain: string) => {
    const timestamp = Date.now();
    return `test-${timestamp}@${domain}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Manager</h1>
        <p className="text-gray-600">Manage throwaway emails for testing and development</p>
      </div>

      {/* Domain Management */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Domains</h2>
          <button
            onClick={() => setShowDomainForm(!showDomainForm)}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Add Domain
          </button>
        </div>

        {showDomainForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded border-l-4 border-blue-500">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Domain (e.g., oh.mom)"
                value={newDomain.domain}
                onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <input
                type="email"
                placeholder="Default email"
                value={newDomain.defaultEmail}
                onChange={(e) => setNewDomain({ ...newDomain, defaultEmail: e.target.value })}
                className="border rounded px-3 py-2"
              />
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={newDomain.isCatchAll}
                  onChange={(e) => setNewDomain({ ...newDomain, isCatchAll: e.target.checked })}
                  className="mr-2"
                />
                Catch-all domain
              </label>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                onClick={addDomain}
                className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
              >
                Add Domain
              </button>
              <button
                onClick={() => setShowDomainForm(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {domains.map((domain) => (
            <div key={domain.id} className="flex items-center justify-between bg-gray-50 p-3 rounded">
              <div className="flex-1">
                <div className="font-medium">{domain.domain}</div>
                <div className="text-sm text-gray-600">
                  Default: {domain.defaultEmail}
                  {domain.isCatchAll && <span className="ml-2 text-green-600">(Catch-all)</span>}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const email = generateEmail(domain.domain);
                    copyToClipboard(email);
                    alert(`Generated and copied: ${email}`);
                  }}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  Generate
                </button>
                <button
                  onClick={() => deleteDomain(domain.id)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Email Entry Form */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold mb-4">Add Email Entry</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            type="email"
            placeholder="Email address"
            value={newEmail.email}
            onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
            className="border rounded px-3 py-2"
          />
          <input
            type="url"
            placeholder="Website URL"
            value={newEmail.url}
            onChange={(e) => setNewEmail({ ...newEmail, url: e.target.value })}
            className="border rounded px-3 py-2"
          />
          <select
            value={newEmail.domain}
            onChange={(e) => setNewEmail({ ...newEmail, domain: e.target.value })}
            className="border rounded px-3 py-2"
          >
            <option value="">Auto-detect domain</option>
            {domains.map((domain) => (
              <option key={domain.id} value={domain.domain}>
                {domain.domain}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={addEmail}
          className="mt-4 bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600"
        >
          Add Email
        </button>
      </div>

      {/* Email List */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h2 className="text-xl font-semibold mb-4">Email Entries ({emails.length})</h2>
        {emails.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No email entries yet. Add one above!</p>
        ) : (
          <div className="space-y-3">
            {emails.map((email) => (
              <div key={email.id} className="flex items-center justify-between bg-gray-50 p-4 rounded">
                <div className="flex-1">
                  <div className="font-medium text-blue-600">{email.email}</div>
                  <div className="text-sm text-gray-600">{email.url}</div>
                  <div className="text-xs text-gray-500">
                    Domain: {email.domain} • Created: {new Date(email.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyToClipboard(email.email)}
                    className="text-blue-500 hover:text-blue-700 text-sm"
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => window.open(email.url, '_blank')}
                    className="text-green-500 hover:text-green-700 text-sm"
                  >
                    Visit
                  </button>
                  <button
                    onClick={() => deleteEmail(email.id)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-blue-600">{emails.length}</div>
          <div className="text-sm text-gray-600">Total Emails</div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-600">{domains.length}</div>
          <div className="text-sm text-gray-600">Domains</div>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg text-center">
          <div className="text-2xl font-bold text-purple-600">
            {domains.filter(d => d.isCatchAll).length}
          </div>
          <div className="text-sm text-gray-600">Catch-all Domains</div>
        </div>
      </div>
    </div>
  );
};